import { useMemo, useEffect, useState } from 'react'
import { DataTable } from 'components/DataTable'
import { useDataTableContext } from 'components/DataTable/DataTableContext'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterIdentifier } from 'models/saved_view'
import { TableSearch } from 'components/Filters/TableSearch'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import VariantsFilterset from 'components/Tables/ProductTable/VariantsFilterset'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import * as S from 'components/Tables/CommonTable.styles'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import { LinkProductCombobox } from './LinkProductCombobox'
import { LinkBrandCombobox, MAX_BULK_ASSIGN_LIMIT } from './LinkBrandCombobox'
import { Link, Unlink, Download, Tag } from 'lucide-react'
import dialogConfirm from 'components/dialogConfirm'
import { notifySuccess } from 'services/toast'
import { BsDownload } from 'react-icons/bs'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { IconMiniButton } from 'components/FbUI/FbButton'

import {
  useListVariantsCatalog,
  useDeleteVariant,
  useBulkMatchVariants,
  type VariantCatalog,
  useTriggerSyndigoSync,
  useGetSyndigoSyncStatus,
  openSyndigoSpecsheet,
  exportVariantsCatalogAsBlob,
  useBulkAssignBrand,
  bulkDownloadSpecsheetsOptimized,
  getBulkDownloadStatus,
} from 'services/products'
import { EditVariantModal } from './EditVariantModal'
import { SyndigoSyncModal } from './SyndigoSyncModal'
import { ManageProductFilesModal } from '../ProductTable/ManageProductFilesModal'
import { useDownload } from 'features/download-widget'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useAuth } from 'context/authentication/useAuth'

import { toast } from 'react-toastify'
import { useVariantTableColumns } from './VariantTableColumns'
import { downloadFile } from 'utils/csvDownloader'

export interface VariantSearchTableInnerProps {
  /**
   * Mode determines behavior:
   * - 'standalone': Used in variants view (for bulk export and bulk matching)
   * - 'selection': Used in product edit modal (for variant matching)
   */
  mode: 'standalone' | 'selection'

  /**
   * Unique key for the table (required for DataTable).
   */
  tableKey: string

  /**
   * Callback when selection changes (selection mode only).
   * Returns array of selected variant IDs.
   */
  onSelectionChange?: (variantIds: number[]) => void

  /**
   * If true, disable selection of variants matched to OTHER products (not current product).
   * Requires currentProductId to be set.
   */
  disableMatchedVariants?: boolean

  /**
   * The current product ID (used with disableMatchedVariants to determine which variants to grey out).
   */
  currentProductId?: number

  /**
   * Show search bar and filters (default: true for standalone, false for selection).
   */
  showFilters?: boolean

  /**
   * Initial selected variant IDs (for pre-selecting matched variants).
   */
  initialSelectedIds?: number[]

  /**
   * Callback when bulk match is triggered (standalone mode only).
   * Called with selected variant IDs and target product ID.
   */
  onBulkMatch?: (variantIds: number[], productId: number) => Promise<void>

  /**
   * Header content to display above the table (standalone mode only).
   * Typically includes title, description, and action buttons.
   */
  tableHeader?: React.JSX.Element
}

const sortableFields: string[] = [
  'name',
  'product_name',
  'sku',
  'format_pack_size',
  'gtin',
  'avg_price',
  'brand_name',
  'category',
  'source',
  'last_synced_at',
]

/**
 * Inner component that uses DataTable context.
 */
export function VariantSearchTableInner({
  mode,
  tableKey,
  onSelectionChange,
  disableMatchedVariants = false,
  currentProductId,
  showFilters = true,
  initialSelectedIds = [],
  tableHeader,
}: VariantSearchTableInnerProps) {
  const { isLimited } = useAuth()
  const { enableBrandManagement } = useFeatureFlagService()
  const VARIANTS_IDENTIFIER = `${tableKey}_VARIANTS` as FilterIdentifier

  // Get filter store for this table (includes pagination!)
  const variantsFilterStore = filterStoreRepo.getStore(VARIANTS_IDENTIFIER)
  const { page, setPage } = variantsFilterStore()
  const filtersAsParams = useFilterParams(variantsFilterStore)

  // Get sorting store for this table
  const { sorting } = dataTableSortingStoreRepo.getStore(tableKey)()

  const pageSize = 50 // Static page size

  // Edit/delete state
  const [selectedVariantId, setSelectedVariantId] = useState<
    number | undefined
  >()

  // Sync modal state
  const [syncingVariant, setSyncingVariant] = useState<VariantCatalog | null>(
    null
  )

  // Manage files modal state
  const [manageFilesVariant, setManageFilesVariant] =
    useState<VariantCatalog | null>(null)

  // Mutation hooks
  const deleteVariantMutation = useDeleteVariant()
  const bulkMatchMutation = useBulkMatchVariants()
  const triggerSyndigoSyncMutation = useTriggerSyndigoSync()

  const bulkAssignBrandMutation = useBulkAssignBrand()

  // Bulk download hook
  const { startDownload, state: downloadState } = useDownload()

  // Convert sorting to ordering string (e.g., [{id: 'name', desc: false}] => 'name' or '-name')
  const ordering = sorting?.[0]
    ? `${sorting[0].desc ? '-' : ''}${sorting[0].id}`
    : undefined

  // Fetch variants with lazy loading
  const { data, isLoading, error, refetch } = useListVariantsCatalog({
    ...filtersAsParams,
    page,
    page_size: pageSize,
    sort: ordering,
  })

  const { refetch: syndigoSyncRefresh } = useGetSyndigoSyncStatus()

  // Get row selection from context
  const tableContext = useDataTableContext()
  const { rowSelection, isAllRowsSelected, totalSelectedRows } =
    tableContext?.state || {}
  const { setRowSelection, setTotalRowsInBackend, clearSelectedRows } =
    tableContext?.methods || {}

  // Pre-select rows based on initialSelectedIds
  useEffect(() => {
    if (
      mode === 'selection' &&
      data?.results &&
      initialSelectedIds.length > 0 &&
      setRowSelection
    ) {
      const initialSelection: Record<string, boolean> = {}
      data.results.forEach((variant, index) => {
        if (variant.id && initialSelectedIds.includes(variant.id)) {
          initialSelection[index.toString()] = true
        }
      })
      // Only set if there are matches
      if (Object.keys(initialSelection).length > 0) {
        console.log(
          'Pre-selecting variants:',
          initialSelection,
          'from IDs:',
          initialSelectedIds
        )
        setRowSelection(initialSelection)
      }
    }
  }, [mode, data?.results, initialSelectedIds, setRowSelection])

  // Notify parent of selection changes in selection mode
  useEffect(() => {
    if (
      mode === 'selection' &&
      onSelectionChange &&
      data?.results &&
      rowSelection
    ) {
      const selectedIds = Object.keys(rowSelection)
        .filter((key) => rowSelection[key])
        .map((key) => {
          const index = parseInt(key)
          return data.results[index]?.id
        })
        .filter((id): id is number => id !== undefined)

      onSelectionChange(selectedIds)
    }
  }, [mode, onSelectionChange, data?.results, rowSelection])

  // Update total rows for "select all" functionality
  useEffect(() => {
    if (data?.count && setTotalRowsInBackend) {
      setTotalRowsInBackend(data.count)
    }
  }, [data?.count, setTotalRowsInBackend])

  // Get selected variant IDs for bulk matching (standalone mode)
  const selectedVariantIds = useMemo(() => {
    if (!data?.results || !rowSelection) return []
    return Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((key) => {
        const index = parseInt(key)
        return data.results[index]?.id
      })
      .filter((id): id is number => id !== undefined)
  }, [data?.results, rowSelection])

  // Get unselected variant IDs (for "select all" with exclusions)
  const unselectedVariantIds = useMemo(() => {
    if (!isAllRowsSelected || !data?.results) return []
    return Object.keys(rowSelection)
      .filter((key) => !rowSelection[key])
      .map((key) => {
        const index = parseInt(key)
        return data.results[index]?.id
      })
      .filter((id): id is number => id !== undefined)
  }, [data?.results, rowSelection, isAllRowsSelected])

  // Callback to clear selection after successful bulk link
  const handleLinkSuccess = () => {
    if (clearSelectedRows) {
      clearSelectedRows()
    }
  }

  const MAX_BULK_MATCH_LIMIT = 30

  // Handler for bulk unlink
  const handleUnlinkVariants = async () => {
    const count = totalSelectedRows || selectedVariantIds.length

    // Enforce limit of 30 items for explicit mode (non-select-all)
    if (
      !isAllRowsSelected &&
      selectedVariantIds.length > MAX_BULK_MATCH_LIMIT
    ) {
      toast.warning(
        `Cannot unlink more than ${MAX_BULK_MATCH_LIMIT} variants at a time. Please select fewer variants.`
      )
      return
    }

    const confirmed = await dialogConfirm(
      `Are you sure you want to unlink ${count} variant${count !== 1 ? 's' : ''} from ${count !== 1 ? 'their' : 'its'} product?`,
      'Unlink Products'
    )

    if (!confirmed) return

    try {
      // If "select all" is active, use filter-based unlink mode
      const requestBody = isAllRowsSelected
        ? {
            unlink_mode: true,
            filters: filtersAsParams,
            exclude_ids: unselectedVariantIds,
          }
        : {
            // Explicit mode - unlink specific variants
            matches: selectedVariantIds.map((variantId) => ({
              variant_id: variantId,
              product_id: null,
            })),
          }

      const result = await bulkMatchMutation.mutateAsync({
        data: requestBody as unknown as Parameters<
          typeof bulkMatchMutation.mutateAsync
        >[0]['data'],
      })

      // Show different message based on async vs sync response.
      // `async` and `matched_count` come from the runtime response but the
      // typed response uses `async_field` (reserved-word workaround).
      const runtimeResult = result as
        | { async?: boolean; matched_count?: number }
        | undefined
      if (runtimeResult?.async) {
        notifySuccess(
          `Unlinking ${count} variant${count !== 1 ? 's' : ''} in background. Changes will appear shortly.`
        )
      } else {
        const matchedCount = runtimeResult?.matched_count || count
        notifySuccess(
          `Successfully unlinked ${matchedCount} variant${matchedCount !== 1 ? 's' : ''}`
        )
      }

      if (clearSelectedRows) {
        clearSelectedRows()
      }
    } catch (err) {
      console.error('Error unlinking variants:', err)
      // Error notification handled by mutation hook
    }
  }

  // Handler for bulk download spec sheets
  const handleBulkDownloadSpecsheets = async () => {
    // Only allow if not already downloading
    if (
      downloadState.status === 'downloading' ||
      downloadState.status === 'preparing' ||
      downloadState.status === 'pending'
    ) {
      toast.info('A download is already in progress')
      return
    }

    // Filter variants that have Syndigo data
    const variantsWithSyndigo = selectedVariantIds.filter((id) => {
      const variant = data?.results.find((v) => v.id === id)
      return variant?.syndigo_product != null
    })

    if (variantsWithSyndigo.length === 0) {
      toast.warning(
        'None of the selected variants have Syndigo data for spec sheets'
      )
      return
    }

    // Build items list for the download widget
    const items = variantsWithSyndigo.map((id) => {
      const variant = data?.results.find((v) => v.id === id)
      return { id, name: variant?.name || `Variant ${id}` }
    })

    // Start the download
    await startDownload({
      items,
      initiate: async (signal) => {
        const response = await bulkDownloadSpecsheetsOptimized(
          variantsWithSyndigo,
          signal
        )
        if ('blob' in response)
          return {
            type: 'blob',
            blob: response.blob,
            filename: response.filename,
          }
        return { type: 'async', requestId: response.request_id }
      },
      getStatus: getBulkDownloadStatus,
      label: 'spec sheets',
    })
  }

  // Handler for edit button
  const handleVariantEdit = (variant: VariantCatalog) => {
    if (!variant) return
    setSelectedVariantId(variant.id)
  }

  // Handler for delete button
  const handleVariantDelete = async (variant: VariantCatalog) => {
    if (!variant?.id) return

    const confirmed = await dialogConfirm(
      `Are you sure you want to delete the variant "${variant.name}"?`,
      'Delete Variant'
    )

    if (!confirmed) return

    try {
      await deleteVariantMutation.mutateAsync({ id: variant.id })
      toast.success('Variant deleted successfully.')
      refetch()
    } catch (err) {
      console.error('Error deleting variant:', err)
      toast.error('Error deleting variant. Please try again later.')
    }
  }

  const handleVariantSync = (variant: VariantCatalog) => {
    if (!variant?.id) return
    setSyncingVariant(variant)
  }

  const handleSyncConfirm = async () => {
    if (!syncingVariant?.id) return

    try {
      await triggerSyndigoSyncMutation.mutateAsync({
        variant_id: syncingVariant.id,
      })
      setSyncingVariant(null)
      refetch()
      syndigoSyncRefresh()
    } catch (err) {
      console.error('Error syncing variant:', err)
      toast.error('Error syncing variant. Please try again later.')
      setSyncingVariant(null)
    }
  }

  const handleSyncCancel = () => {
    if (!triggerSyndigoSyncMutation.isPending) {
      setSyncingVariant(null)
    }
  }

  const handleOpenSyndigo = async (variant: VariantCatalog) => {
    if (!variant?.syndigo_product?.syndigo_product_id) return

    const syndigoUrl = `https://platform.syndigo.com/products/detail/${variant.syndigo_product.syndigo_product_id}`

    const link = document.createElement('a')
    link.href = syndigoUrl
    link.setAttribute('rel', 'noreferrer')
    link.setAttribute('target', '_blank')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleOpenSpecsheet = async (variant: VariantCatalog) => {
    if (!variant?.id || !variant?.syndigo_product) return

    try {
      await openSyndigoSpecsheet(variant.id)
    } catch (err) {
      console.error('Error opening spec sheet:', err)
      toast.error('Error loading spec sheet. Please try again later.')
    }
  }

  const handleManageFiles = (variant: VariantCatalog) => {
    if (!variant?.id) return
    setManageFilesVariant(variant)
  }

  const handleDownloadSpecsheet = async (variant: VariantCatalog) => {
    if (!variant?.id || !variant?.syndigo_product) return

    // Only allow if not already downloading
    if (
      downloadState.status === 'downloading' ||
      downloadState.status === 'preparing' ||
      downloadState.status === 'pending'
    ) {
      toast.info('A download is already in progress')
      return
    }

    // Use download widget for individual downloads to track progress
    await startDownload({
      items: [{ id: variant.id, name: variant.name }],
      initiate: async (signal) => {
        const response = await bulkDownloadSpecsheetsOptimized(
          [variant.id],
          signal
        )
        if ('blob' in response)
          return {
            type: 'blob',
            blob: response.blob,
            filename: response.filename,
          }
        return { type: 'async', requestId: response.request_id }
      },
      getStatus: getBulkDownloadStatus,
      label: 'spec sheets',
    })
  }

  // Handler for bulk unlink brand
  const handleUnlinkBrand = async () => {
    const count = totalSelectedRows || selectedVariantIds.length

    if (
      !isAllRowsSelected &&
      selectedVariantIds.length > MAX_BULK_ASSIGN_LIMIT
    ) {
      toast.warning(
        `Cannot unlink brand from more than ${MAX_BULK_ASSIGN_LIMIT} variants at a time. Please select fewer variants.`
      )
      return
    }

    const confirmed = await dialogConfirm(
      `Are you sure you want to remove the brand from ${count} variant${count !== 1 ? 's' : ''}?`,
      'Unlink Brand'
    )

    if (!confirmed) return

    try {
      const requestBody = isAllRowsSelected
        ? {
            brand_id: null as null,
            filters: filtersAsParams,
            exclude_ids: unselectedVariantIds,
          }
        : {
            brand_id: null as null,
            variant_ids: selectedVariantIds,
          }

      const result = await bulkAssignBrandMutation.mutateAsync({
        data: requestBody as unknown as Parameters<
          typeof bulkAssignBrandMutation.mutateAsync
        >[0]['data'],
      })

      const updatedCount =
        (result as { updated_count?: number })?.updated_count ?? count
      notifySuccess(
        `Successfully removed brand from ${updatedCount} variant${updatedCount !== 1 ? 's' : ''}`
      )

      if (clearSelectedRows) {
        clearSelectedRows()
      }
    } catch (err) {
      console.error('Error unlinking brand:', err)
      toast.error('Failed to unlink brand. Please try again.')
    }
  }

  const handleExport = async () => {
    try {
      // All available columns for variants export
      const columns = [
        'name',
        'sku',
        'gtin',
        'description',
        'product_name',
        'match_status',
        'format_pack_size',
        'units_per_case',
        'case_weight',
        'case_weight_unit',
        'unit_weight',
        'unit_weight_unit',
        'avg_price',
        'created',
        'modified',
      ]

      const variantsSortingStore = dataTableSortingStoreRepo.getStore(
        `${tableKey}-variants`
      )
      const variantsSorting = variantsSortingStore.getState().sorting

      const variantsSortParams: Record<string, unknown> = {}
      if (variantsSorting?.length) {
        const sortField = variantsSorting[0]?.id
        variantsSortParams['sort'] = variantsSorting[0].desc
          ? '-' + sortField
          : sortField
      }

      // Determine include_ids and exclude_ids based on selection state
      const includeIds = isAllRowsSelected ? [] : selectedVariantIds
      const excludeIds = isAllRowsSelected ? unselectedVariantIds : []

      const params: Record<string, any> = {
        ...filtersAsParams,
        ...variantsSortParams,
      }

      const blob = await exportVariantsCatalogAsBlob(
        {
          selected_columns: columns,
          export_type: 'csv',
          ...(includeIds.length > 0 && { include_ids: includeIds }),
          ...(excludeIds.length > 0 && { exclude_ids: excludeIds }),
        },
        params
      )

      downloadFile(blob, 'variants_export.csv')
      clearSelectedRows?.()
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export variants. Please try again.')
    }
  }

  const columns = useVariantTableColumns({
    handleOpenSyndigo,
    handleVariantDelete,
    handleVariantEdit,
    handleVariantSync,
    handleOpenSpecsheet,
    handleManageFiles,
    handleDownloadSpecsheet,
    isReadOnly: isLimited,
  })

  // Don't filter - show all variants but we'll grey out matched ones in the row config
  const tableData = useMemo(() => {
    if (!data?.results) return []
    return data.results
  }, [data?.results])

  // Disable selection for variants matched to OTHER products (not current product)
  const disabledRowsIdx = useMemo(() => {
    if (
      mode === 'selection' &&
      disableMatchedVariants &&
      data?.results &&
      currentProductId
    ) {
      return data.results
        .map((variant, idx) => {
          // Only disable if matched AND not matched to current product
          const isMatchedToOther =
            variant.match_status === 'matched' &&
            variant.product_id !== null &&
            variant.product_id !== currentProductId
          return isMatchedToOther ? idx : -1
        })
        .filter((idx) => idx !== -1)
    }
    return []
  }, [mode, disableMatchedVariants, data?.results, currentProductId])

  // Style variants matched to OTHER products as greyed out
  const rowAttributes = useMemo(() => {
    if (mode === 'selection' && disableMatchedVariants && currentProductId) {
      return (row: any) => {
        const isMatchedToOther =
          row.original.match_status === 'matched' &&
          row.original.product_id !== null &&
          row.original.product_id !== currentProductId
        if (isMatchedToOther) {
          return {
            className: 'opacity-50 pointer-events-none',
          } as any
        }
        return {} as any
      }
    }
    return undefined
  }, [mode, disableMatchedVariants, currentProductId])

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-red-600">
        Error loading variants: {error.message}
      </div>
    )
  }

  return (
    <>
      <DataTable
        tableKey={tableKey}
        tableHeader={
          <>
            {tableHeader}
            {showFilters && (
              <S.SearchContainer>
                <S.SearchContainerFilters>
                  <S.TableSearchContainer>
                    <TableSearch
                      filterStore={variantsFilterStore}
                      searchPlaceholder="Search by product name, variant name, SKU, UPC/GTIN, or brand..."
                      tableKey={tableKey}
                    />
                  </S.TableSearchContainer>
                  <S.TableButtonsContainer>
                    <VariantsFilterset filterIdentifier={VARIANTS_IDENTIFIER} />
                    <ColumnSelectorRecipient tableKey={tableKey} />
                  </S.TableButtonsContainer>
                </S.SearchContainerFilters>
                <div className="flex flex-row justify-between gap-4">
                  {/* Bulk Actions (standalone mode only) */}
                  {mode === 'standalone' && (
                    <TableButtonAction
                      selectedRowsCount={
                        totalSelectedRows || selectedVariantIds.length
                      }
                      entityType="variants"
                      items={[
                        ...(!isLimited
                          ? [
                              {
                                label: 'Link to Product',
                                icon: <Link size={20} />,
                                subContent: (
                                  <LinkProductCombobox
                                    selectedVariantIds={selectedVariantIds}
                                    unselectedVariantIds={unselectedVariantIds}
                                    totalSelectedRows={
                                      totalSelectedRows ||
                                      selectedVariantIds.length
                                    }
                                    isAllRowsSelected={
                                      isAllRowsSelected || false
                                    }
                                    filters={filtersAsParams}
                                    onSuccess={handleLinkSuccess}
                                    buttonLess={true}
                                  />
                                ),
                              },
                              {
                                label: 'Unlink Products',
                                icon: <Unlink size={20} />,
                                callback: handleUnlinkVariants,
                              },
                            ]
                          : []),
                        ...(!isLimited && enableBrandManagement
                          ? [
                              {
                                label: 'Assign Brand',
                                icon: <Tag size={20} />,
                                subContent: (
                                  <LinkBrandCombobox
                                    selectedVariantIds={selectedVariantIds}
                                    unselectedVariantIds={unselectedVariantIds}
                                    totalSelectedRows={
                                      totalSelectedRows ||
                                      selectedVariantIds.length
                                    }
                                    isAllRowsSelected={
                                      isAllRowsSelected || false
                                    }
                                    filters={filtersAsParams}
                                    onSuccess={handleLinkSuccess}
                                    buttonLess={true}
                                  />
                                ),
                              },
                              {
                                label: 'Unlink Brand',
                                icon: <Unlink size={20} />,
                                callback: handleUnlinkBrand,
                              },
                            ]
                          : []),
                        {
                          label: 'Download Spec Sheets',
                          icon: <Download size={20} />,
                          callback: handleBulkDownloadSpecsheets,
                        },
                        {
                          label: 'Export',
                          icon: <BsDownload size={20} />,
                          callback: handleExport,
                        },
                      ]}
                    />
                  )}
                  <FilterChips
                    identifier={VARIANTS_IDENTIFIER}
                    store={variantsFilterStore}
                    clearAllButton
                    showActive
                  />
                </div>
              </S.SearchContainer>
            )}
          </>
        }
        data={tableData}
        columns={columns}
        loading={isLoading}
        enableRowSelection={true}
        disabledRowsIdx={disabledRowsIdx}
        rowAttributes={rowAttributes}
        footerControls={
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-export">
                {totalSelectedRows === 0
                  ? 'Export all products variants'
                  : `Export ${totalSelectedRows} selected product variant${totalSelectedRows !== 1 ? 's' : ''}`}
              </Tooltip>
            }
          >
            <div>
              <IconMiniButton onClick={handleExport}>
                <BsDownload size={18} />
              </IconMiniButton>
            </div>
          </OverlayTrigger>
        }
        sortableFields={sortableFields}
        virtualizeRows={false}
        isPaginationEnabled={true}
        selectAllText="Select Variants"
        paginationOptions={{
          isPaginationLoading: isLoading,
          setPage: setPage,
          page: page,
          totalRows: data?.count ?? 0,
          pageSize: pageSize,
        }}
      />

      {/* Edit Variant Modal */}
      {!!selectedVariantId && (
        <EditVariantModal
          show={!!selectedVariantId}
          variantId={selectedVariantId}
          onHide={() => {
            setSelectedVariantId(undefined)
          }}
          onSuccess={() => refetch()}
        />
      )}

      {/* Syndigo Sync Modal */}
      {syncingVariant && (
        <SyndigoSyncModal
          show={!!syncingVariant}
          variantName={syncingVariant.name}
          isLoading={triggerSyndigoSyncMutation.isPending}
          onConfirm={handleSyncConfirm}
          onCancel={handleSyncCancel}
        />
      )}

      {/* Manage Files Modal */}
      {manageFilesVariant && (
        <ManageProductFilesModal
          show={!!manageFilesVariant}
          onClose={() => setManageFilesVariant(null)}
          variantId={manageFilesVariant.id}
          variantName={manageFilesVariant.name}
          productId={manageFilesVariant.product_id}
          productName={manageFilesVariant.product_name}
        />
      )}
    </>
  )
}
